import { parseAuthorizationAttempt } from 'app/util/oauthAuthorization';

// SSO leaves the SPA for the IdP, which drops router state, so an OAuth attempt that sent the user to login is kept
// here until an /sso/... URL is built. The server validates the forwarded value again.
const STORAGE_KEY = 'OAuthSsoReturnTo';
// The Authorization Server abandons an attempt after five minutes, so an older one is not worth resuming.
const MAX_AGE_MS = 5 * 60 * 1000;

interface AttemptLocation {
  pathname?: string;
  search?: string;
}

function attemptPath(location?: AttemptLocation): string | undefined {
  if (location?.pathname !== '/oauth/authorize') {
    return undefined;
  }
  const transactionId = parseAuthorizationAttempt(location.search ?? '');
  return transactionId ? `/oauth/authorize?transaction=${transactionId}` : undefined;
}

export function clearOAuthSsoReturn() {
  sessionStorage.removeItem(STORAGE_KEY);
}

// Called with the location that sent the user to login: remembers an OAuth attempt, forgets it for anything else.
export function rememberOAuthSsoReturn(from?: AttemptLocation) {
  if (!from) {
    return;
  }
  const returnTo = attemptPath(from);
  if (returnTo) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ returnTo, savedAt: Date.now() }));
  } else {
    clearOAuthSsoReturn();
  }
}

function storedReturnTo(): string | undefined {
  try {
    const { returnTo, savedAt } = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') ?? {};
    const age = Date.now() - savedAt;
    if (typeof returnTo === 'string' && age >= 0 && age <= MAX_AGE_MS) {
      const url = new URL(returnTo, window.location.origin);
      return url.origin === window.location.origin ? attemptPath(url) : undefined;
    }
  } catch {
    // Unreadable entries are treated as absent.
  }
  return undefined;
}

// Adds the remembered OAuth attempt to an /sso/... start URL so the SSO callback can resume it.
export function withOAuthSsoReturn(path: string): string {
  const returnTo = storedReturnTo();
  if (!returnTo) {
    return path;
  }
  const url = new URL(path, window.location.origin);
  url.searchParams.set('returnTo', returnTo);
  return `${url.pathname}${url.search}`;
}
